import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Chip,
  Grid,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import {
  NavigateNext,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Source as SourceIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/axios';

interface Lead {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  company: string;
  position: string;
  source: string;
  status: string;
  priority: string;
  description: string;
  notes: string;
  estimatedValue: number;
  followUpDate: string;
  createdAt: string;
  updatedAt: string;
}

const LeadDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [lead, setLead] = useState<Lead | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState<Partial<Lead>>({});

  useEffect(() => {
    if (id) {
      loadLead(id);
    }
  }, [id]);

  const loadLead = async (leadId: string) => {
    try {
      const response = await api.get(`/leads/${leadId}`);
      if (response.data.success) {
        setLead(response.data.data);
        setEditData(response.data.data);
      } else {
        setError('Failed to load lead details');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load lead details');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    setEditData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSave = async () => {
    if (!lead) return;
    
    try {
      const response = await api.put(`/leads/${lead.id}`, editData);
      if (response.data.success) {
        setLead(response.data.data);
        setEditMode(false);
      } else {
        setError('Failed to update lead');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update lead');
    }
  };

  const handleCancel = () => {
    setEditData(lead || {});
    setEditMode(false);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT': return 'error';
      case 'HIGH': return 'warning';
      case 'MEDIUM': return 'info';
      case 'LOW': return 'default';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'NEW': return 'primary';
      case 'CONTACTED': return 'info';
      case 'QUALIFIED': return 'success';
      case 'LOST': return 'error';
      case 'CONVERTED': return 'success';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!lead) {
    return (
      <Box p={3}>
        <Alert severity="info">Lead not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumb */}
      <Breadcrumbs 
        separator={<NavigateNext fontSize="small" />} 
        sx={{ mb: 3 }}
      >
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/')}
          sx={{ cursor: 'pointer' }}
        >
          Dashboard
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/leads')}
          sx={{ cursor: 'pointer' }}
        >
          Leads
        </Link>
        <Typography color="text.primary">
          {lead.firstName} {lead.lastName}
        </Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Lead Details
        </Typography>
        <Box>
          {editMode ? (
            <>
              <IconButton color="primary" onClick={handleSave}>
                <SaveIcon />
              </IconButton>
              <IconButton onClick={handleCancel}>
                <CancelIcon />
              </IconButton>
            </>
          ) : (
            <IconButton onClick={() => setEditMode(true)}>
              <EditIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon />
                Basic Information
              </Typography>
              
              {editMode ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <TextField
                      name="firstName"
                      label="First Name"
                      value={editData.firstName || ''}
                      onChange={handleInputChange}
                      fullWidth
                    />
                    <TextField
                      name="lastName"
                      label="Last Name"
                      value={editData.lastName || ''}
                      onChange={handleInputChange}
                      fullWidth
                    />
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <TextField
                      name="email"
                      label="Email"
                      value={editData.email || ''}
                      onChange={handleInputChange}
                      fullWidth
                    />
                    <TextField
                      name="phone"
                      label="Phone"
                      value={editData.phone || ''}
                      onChange={handleInputChange}
                      fullWidth
                    />
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Typography><strong>Name:</strong> {lead.firstName} {lead.lastName}</Typography>
                  <Typography><strong>Email:</strong> {lead.email || 'Not provided'}</Typography>
                  <Typography><strong>Phone:</strong> {lead.phone || 'Not provided'}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Company Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon />
                Company Information
              </Typography>
              
              {editMode ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    name="company"
                    label="Company"
                    value={editData.company || ''}
                    onChange={handleInputChange}
                    fullWidth
                  />
                  <TextField
                    name="position"
                    label="Position"
                    value={editData.position || ''}
                    onChange={handleInputChange}
                    fullWidth
                  />
                </Box>
              ) : (
                <Box>
                  <Typography><strong>Company:</strong> {lead.company || 'Not provided'}</Typography>
                  <Typography><strong>Position:</strong> {lead.position || 'Not provided'}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Lead Details */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SourceIcon />
                Lead Details
              </Typography>
              
              {editMode ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Source</InputLabel>
                    <Select
                      name="source"
                      value={editData.source || ''}
                      label="Source"
                      onChange={handleSelectChange}
                    >
                      <MenuItem value="WEBSITE">Website</MenuItem>
                      <MenuItem value="REFERRAL">Referral</MenuItem>
                      <MenuItem value="SOCIAL_MEDIA">Social Media</MenuItem>
                      <MenuItem value="ADVERTISING">Advertising</MenuItem>
                      <MenuItem value="COLD_CALL">Cold Call</MenuItem>
                      <MenuItem value="NETWORKING">Networking</MenuItem>
                      <MenuItem value="OTHER">Other</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      name="status"
                      value={editData.status || ''}
                      label="Status"
                      onChange={handleSelectChange}
                    >
                      <MenuItem value="NEW">New</MenuItem>
                      <MenuItem value="CONTACTED">Contacted</MenuItem>
                      <MenuItem value="QUALIFIED">Qualified</MenuItem>
                      <MenuItem value="LOST">Lost</MenuItem>
                      <MenuItem value="CONVERTED">Converted</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      name="priority"
                      value={editData.priority || ''}
                      label="Priority"
                      onChange={handleSelectChange}
                    >
                      <MenuItem value="LOW">Low</MenuItem>
                      <MenuItem value="MEDIUM">Medium</MenuItem>
                      <MenuItem value="HIGH">High</MenuItem>
                      <MenuItem value="URGENT">Urgent</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              ) : (
                <Box>
                  <Box sx={{ mb: 2 }}>
                    <Chip 
                      label={lead.source} 
                      color="primary" 
                      size="small" 
                      sx={{ mr: 1 }}
                    />
                    <Chip 
                      label={lead.status} 
                      color={getStatusColor(lead.status) as any} 
                      size="small" 
                      sx={{ mr: 1 }}
                    />
                    <Chip 
                      label={lead.priority} 
                      color={getPriorityColor(lead.priority) as any} 
                      size="small" 
                    />
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Financial Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MoneyIcon />
                Financial Information
              </Typography>
              
              {editMode ? (
                <TextField
                  name="estimatedValue"
                  label="Estimated Value ($)"
                  type="number"
                  value={editData.estimatedValue || ''}
                  onChange={handleInputChange}
                  fullWidth
                />
              ) : (
                <Typography>
                  <strong>Estimated Value:</strong> ${lead.estimatedValue?.toLocaleString() || 'Not specified'}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Address */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon />
                Address
              </Typography>
              
              {editMode ? (
                <TextField
                  name="address"
                  label="Address"
                  value={editData.address || ''}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={3}
                />
              ) : (
                <Typography>{lead.address || 'Not provided'}</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Follow-up Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScheduleIcon />
                Follow-up Information
              </Typography>
              
              {editMode ? (
                <TextField
                  name="followUpDate"
                  label="Follow-up Date"
                  type="date"
                  value={editData.followUpDate ? editData.followUpDate.split('T')[0] : ''}
                  onChange={handleInputChange}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              ) : (
                <Typography>
                  <strong>Follow-up Date:</strong> {lead.followUpDate ? new Date(lead.followUpDate).toLocaleDateString() : 'Not scheduled'}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Description and Notes */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Description & Notes
              </Typography>
              
              {editMode ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    name="description"
                    label="Description"
                    value={editData.description || ''}
                    onChange={handleInputChange}
                    fullWidth
                    multiline
                    rows={3}
                  />
                  <TextField
                    name="notes"
                    label="Notes"
                    value={editData.notes || ''}
                    onChange={handleInputChange}
                    fullWidth
                    multiline
                    rows={3}
                  />
                </Box>
              ) : (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Description:</Typography>
                  <Typography sx={{ mb: 2 }}>{lead.description || 'No description provided'}</Typography>
                  
                  <Typography variant="subtitle2" gutterBottom>Notes:</Typography>
                  <Typography>{lead.notes || 'No notes provided'}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LeadDetails; 